import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import { GET_REQUIREMENT, GET_REQUIREMENTS, GET_STATS, UPDATE_REQUIREMENT, DELETE_REQUIREMENT, GET_ENTITY_HISTORY, GET_SOURCES, GET_USERS, LINK_REQUIREMENT_DRIVER, UNLINK_REQUIREMENT_DRIVER } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";
import { Badge, completionVariant, completionLabel } from "@/components/Badge";
import { TagSelector } from "@/components/TagSelector";
import { selectableOwnerNames } from "@/users";
import { useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import type { Requirement, Source } from "@/types";

export function RequirementDetail() {
  const { activeId, navigateTo, navigateToCreate, activeUser, activeProject } = useApp();
  const client = useApolloClient();
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Requirement>>({});
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editRequirementDriverIds, setEditRequirementDriverIds] = useState<string[]>([]);
  const [editSourceDriverIds, setEditSourceDriverIds] = useState<string[]>([]);
  const editSourceDriverIdsRef = useRef<string[]>([]);
  const pendingSourceDriverIdRef = useRef("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  useEffect(() => {
    setEditing(false);
    setEditData({});
    setEditTags([]);
    setEditRequirementDriverIds([]);
    setEditSourceDriverIds([]);
    editSourceDriverIdsRef.current = [];
    pendingSourceDriverIdRef.current = "";
    setDeleteConfirm(false);
  }, [activeId]);

  const { data: reqRaw, loading: isLoading } = useQuery<any>(GET_REQUIREMENT, {
    variables: { id: activeId! },
    skip: !activeId,
    fetchPolicy: "network-only",
  });

  const { data: allReqsRaw } = useQuery<any>(GET_REQUIREMENTS);

  const { data: statsRaw } = useQuery<any>(GET_STATS);

  const { data: sourcesRaw } = useQuery<any>(GET_SOURCES);
  const { data: usersRaw } = useQuery<any>(GET_USERS);

  const { data: historyRaw } = useQuery<any>(GET_ENTITY_HISTORY, {
    variables: { entityType: "requirement", entityId: activeId!, limit: 20 },
    skip: !activeId,
  });

  const [updateMutation] = useMutation<any>(UPDATE_REQUIREMENT, {
    onCompleted: async () => {
      setEditing(false);
      client.refetchQueries({ include: [GET_STATS] });
    },
    onError: (err) => {
      console.error("Update failed:", err.message);
      alert(`Save failed: ${err.message}`);
    },
  });

  const [deleteMutation] = useMutation<any>(DELETE_REQUIREMENT, {
    onCompleted: async () => {
      await client.refetchQueries({ include: [GET_REQUIREMENTS, GET_STATS] });
      navigateTo("requirements");
    },
  });

  const [linkRequirementDriver] = useMutation<any>(LINK_REQUIREMENT_DRIVER);
  const [unlinkRequirementDriver] = useMutation<any>(UNLINK_REQUIREMENT_DRIVER);

  const req: Requirement | undefined = reqRaw ? mapToSnakeCase(reqRaw.requirement) : undefined;
  const stats = statsRaw ? mapToSnakeCase(statsRaw.stats) : undefined;
  const allSources: Source[] = sourcesRaw ? sourcesRaw.sources : [];
  const ownerOptions: string[] = usersRaw ? selectableOwnerNames(mapToSnakeCase(usersRaw.users).users) : [];

  if (!activeId) return null;
  if (isLoading) return <div className="p-5 text-text2">Loading...</div>;
  if (!req) return <div className="p-5 text-text2">Not found</div>;

  const canEdit = activeUser?.role === "manager" || activeUser?.role === "admin" || (activeUser?.role === "lead" && activeUser?.owns?.includes(req.id));
  const hasDrivenRequirements = req.driven_requirements && req.driven_requirements.length > 0;
  const canCreateChild = req.level !== "implementation";

  const allRequirements: Requirement[] = allReqsRaw ? mapToSnakeCase(allReqsRaw.requirements) : [];

  const sourceById = new Map<string, Source>();
  for (const source of [...(req.sources || []), ...allSources]) {
    if (source.id) sourceById.set(source.id, source);
  }
  const displaySourceDrivers = req.source_drivers && req.source_drivers.length > 0
    ? req.source_drivers.map(driver => ({ ...driver, url: sourceById.get(driver.id)?.url }))
    : (req.sources || []).map(source => ({ id: source.id, label: source.label, url: source.url }));

  function startEdit() {
    if (!req) return;
    const sourceDriverIds = (req.sources || []).map(sourceId).filter(Boolean);
    editSourceDriverIdsRef.current = sourceDriverIds;
    pendingSourceDriverIdRef.current = "";
    setEditSourceDriverIds(sourceDriverIds);
    setEditRequirementDriverIds((req.requirement_drivers || []).map(driver => driver.id));
    setEditTags(req.tags || []);
    setEditData({ title: req.title || "", description: req.description, completion: req.completion, requirement_level: req.requirement_level || req.level, discipline: req.discipline, category: req.category, owner: req.owner, revision: req.revision });
    setEditing(true);
  }

  function setSourceDrivers(nextSourceDriverIds: string[]) {
    editSourceDriverIdsRef.current = nextSourceDriverIds;
    setEditSourceDriverIds(nextSourceDriverIds);
  }

  function saveEdit() {
    if (!req) return;
    const sourceDriverIds = pendingSourceDriverIdRef.current && !editSourceDriverIdsRef.current.includes(pendingSourceDriverIdRef.current)
      ? [...editSourceDriverIdsRef.current, pendingSourceDriverIdRef.current]
      : editSourceDriverIdsRef.current;
    const updateInput = {
      title: editData.title,
      description: editData.description,
      requirementLevel: editData.requirement_level,
      discipline: editData.discipline,
      category: editData.category,
      owner: editData.owner,
      revision: editData.revision,
      tags: editTags,
      sources: sourceDriverIds,
    };
    updateMutation({
      variables: {
        id: activeId!,
        input: updateInput,
      },
    }).then(async () => {
      updateRequirementCache(req, updateInput, sourceById);
      const currentDrivers = new Set((req.requirement_drivers || []).map(driver => driver.id));
      const nextDrivers = new Set(editRequirementDriverIds);
      const mutations: Promise<unknown>[] = [];
      for (const driverId of nextDrivers) {
        if (!currentDrivers.has(driverId)) {
          mutations.push(linkRequirementDriver({ variables: { requirementId: activeId!, driverRequirementId: driverId } }));
        }
      }
      for (const driverId of currentDrivers) {
        if (!nextDrivers.has(driverId)) {
          mutations.push(unlinkRequirementDriver({ variables: { requirementId: activeId!, driverRequirementId: driverId } }));
        }
      }
      await Promise.all(mutations);
      if (mutations.length > 0) {
        await client.refetchQueries({ include: [GET_REQUIREMENT, GET_REQUIREMENTS, GET_STATS] });
      }
    }).catch(() => undefined);
  }

  function updateRequirementCache(
    current: Requirement,
    input: {
      title?: string;
      description?: string;
      requirementLevel?: string;
      discipline?: string;
      category?: string;
      owner?: string;
      revision?: string;
      tags: string[];
      sources: string[];
    },
    sourceLookup: Map<string, Source>,
  ) {
    const nextSources = input.sources.map(id => sourceLookup.get(id) || { id, label: id, url: "" });
    const nextSourceDrivers = nextSources.map(source => ({
      driverType: "source",
      id: source.id,
      label: source.label,
      active: true,
    }));
    const patch = {
      title: input.title ?? "",
      description: input.description ?? "",
      requirementLevel: input.requirementLevel || current.requirement_level || current.level,
      discipline: input.discipline ?? "",
      category: input.category ?? "",
      owner: input.owner ?? "",
      revision: input.revision ?? "",
      tags: input.tags,
      sources: nextSources,
      sourceDrivers: nextSourceDrivers,
    };
    client.cache.updateQuery<{ requirement: any | null }>({ query: GET_REQUIREMENT, variables: { id: current.id } }, data => {
      if (!data?.requirement) return data;
      return { ...data, requirement: { ...data.requirement, ...patch } };
    });
    const cacheId = client.cache.identify({ __typename: "Requirement", id: current.id });
    if (cacheId) {
      client.cache.modify({
        id: cacheId,
        fields: {
          title: () => patch.title,
          description: () => patch.description,
          requirementLevel: () => patch.requirementLevel,
          discipline: () => patch.discipline,
          category: () => patch.category,
          owner: () => patch.owner,
          revision: () => patch.revision,
          tags: () => patch.tags,
          sources: () => patch.sources,
          sourceDrivers: () => patch.sourceDrivers,
        },
      });
    }
    client.cache.updateQuery<{ requirements: any[] }>({ query: GET_REQUIREMENTS }, data => {
      if (!data?.requirements) return data;
      return {
        ...data,
        requirements: data.requirements.map((item: any) => item.id === current.id ? { ...item, ...patch } : item),
      };
    });
    client.cache.modify({
      fields: {
        requirements(existing = []) {
          if (!Array.isArray(existing)) return existing;
          return existing.map((item: any) => item?.id === current.id ? { ...item, ...patch } : item);
        },
      },
    });
  }

  return (
    <div data-testid="requirement-detail">
      {/* ID */}
      <div className="mb-5 text-[11px] text-accent font-semibold uppercase tracking-[0.3px]">{req.id}</div>

      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex gap-2 flex-wrap">
            <Badge>{req.level}</Badge>
            <Badge variant={completionVariant(req.completion)}>{completionLabel(req.completion)}</Badge>
            {req.discipline && <Badge variant="info">{req.discipline}</Badge>}
            {req.category && <Badge variant="muted">{req.category}</Badge>}
          </div>
          {canEdit && !editing && (
            <div className="flex items-center gap-1.5">
              <button className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer flex items-center gap-1 transition-colors hover:border-accent hover:text-text hover:bg-bg3" onClick={startEdit}>Edit</button>
              <button
                className="px-[10px] py-[5px] border border-red/40 rounded-default bg-transparent text-red text-xs cursor-pointer flex items-center gap-1 transition-colors hover:border-red hover:bg-red/10"
                aria-label="Delete requirement"
                onClick={() => setDeleteConfirm(true)}
              >
                Delete
              </button>
              {canCreateChild && (
                <button
                  className="px-[10px] py-[5px] border border-accent rounded-default bg-accent text-white text-xs cursor-pointer flex items-center gap-1 transition-colors hover:bg-accent2 hover:border-accent2"
                  onClick={() => navigateToCreate(req.id)}
                >
                  + New
                </button>
              )}
            </div>
          )}
          {deleteConfirm && (
            <div className="mt-2 p-3 border border-red/30 rounded-default bg-red/5">
              {hasDrivenRequirements ? (
                <>
                  <p className="text-xs text-red font-semibold mb-1">Cannot delete this requirement</p>
                  <p className="text-xs text-text2">This requirement drives {req.driven_requirements!.length} requirement{req.driven_requirements!.length > 1 ? "s" : ""}. Remove those driver links first before deleting this requirement.</p>
                  <div className="flex gap-2 mt-2">
                    <button className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer transition-colors hover:border-accent hover:text-text" onClick={() => setDeleteConfirm(false)}>OK</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-red font-semibold mb-1">Are you sure you want to delete {req.id}?</p>
                  <p className="text-xs text-text2">This action cannot be undone. The requirement and all its test links will be permanently removed.</p>
                  <div className="flex gap-2 mt-2">
                    <button
                      className="px-[10px] py-[5px] border border-red rounded-default bg-red text-white text-xs cursor-pointer font-semibold transition-colors hover:bg-red/80"
                      onClick={() => {
                        deleteMutation({ variables: { id: req.id, projectId: activeProject, userId: activeUser!.id } });
                        setDeleteConfirm(false);
                      }}
                    >
                      Yes, delete permanently
                    </button>
                    <button className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer transition-colors hover:border-accent hover:text-text" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="mb-5">
        <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Description</h3>
        {editing ? (
          <textarea
            value={editData.description || ""}
            onChange={e => setEditData({ ...editData, description: e.target.value })}
            className="w-full bg-bg border border-border rounded-default px-3 py-2 text-text text-xs min-h-[80px] resize-y"
          />
        ) : (
          <p className="text-[13px] text-text leading-[1.6]">{req.description || <span className="text-text2 italic">No description</span>}</p>
        )}
      </div>

      {/* Properties */}
      {!editing && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Properties</h3>
          <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-xs [&_dt]:text-text2 [&_dd]:text-text">
            <dt>Title</dt><dd>{req.title || "—"}</dd>
            <dt>Level</dt><dd>{req.requirement_level || req.level}</dd>
            <dt>Discipline</dt><dd>{req.discipline || "—"}</dd>
            <dt>Category</dt><dd>{req.category || "—"}</dd>
            <dt>Owner</dt><dd>{req.owner || "—"}</dd>
            <dt>Revision</dt><dd>{req.revision || "—"}</dd>
            <dt>Tags</dt><dd><TagsList tags={req.tags} /></dd>
            <dt>Correctness</dt><dd>{req.correctness || "—"}</dd>
            <dt>Requirement Drivers</dt><dd><DriverList drivers={req.requirement_drivers} onNavigate={(id) => navigateTo("requirements", id)} testId="requirement-drivers-list" /></dd>
            <dt>Source Drivers</dt><dd><DriverList drivers={displaySourceDrivers} testId="sources-list" /></dd>
          </div>
        </div>
      )}

      {/* Edit controls */}
      {editing && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Edit Fields</h3>
          <div className="flex flex-col gap-2">
            <EditField label="Title" value={editData.title || ""} onChange={v => setEditData({ ...editData, title: v })} />
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-text2 w-20">Level</label>
              <select
                value={editData.requirement_level || "implementation"}
                onChange={e => setEditData({ ...editData, requirement_level: e.target.value })}
                className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs"
                aria-label="Requirement level"
              >
                {REQUIREMENT_LEVELS.map(level => <option key={level} value={level}>{level}</option>)}
              </select>
            </div>
            {canEdit && (
              <SourceSelector
                currentSourceDrivers={editSourceDriverIds.map(id => sourceById.get(id) || { id, label: id, url: "" })}
                allSourceDrivers={allSources}
                onLink={(sourceId) => {
                  const sourceDriverIds = editSourceDriverIdsRef.current;
                  if (!sourceDriverIds.includes(sourceId)) setSourceDrivers([...sourceDriverIds, sourceId]);
                }}
                onUnlink={(sourceId) => setSourceDrivers(editSourceDriverIdsRef.current.filter(id => id !== sourceId))}
                pendingSourceDriverIdRef={pendingSourceDriverIdRef}
              />
            )}
            <RequirementDriverSelector
              currentDriverIds={editRequirementDriverIds}
              allRequirements={allRequirements}
              currentRequirementId={req.id}
              onChange={setEditRequirementDriverIds}
            />
            <EditField label="Discipline" value={editData.discipline || ""} onChange={v => setEditData({ ...editData, discipline: v })} />
            <EditField label="Category" value={editData.category || ""} onChange={v => setEditData({ ...editData, category: v })} />
            <EditField label="Owner" value={editData.owner || ""} onChange={v => setEditData({ ...editData, owner: v })} type="select" options={["", ...ownerOptions]} />
            <div className="flex items-start gap-2">
              <label className="text-[11px] text-text2 w-20 pt-1">Tags</label>
              <div className="flex-1">
                <TagSelector
                  selectedTags={editTags}
                  availableTags={stats?.tags || []}
                  onChange={setEditTags}
                  compact
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button className="px-[10px] py-[5px] border border-accent rounded-default bg-accent text-white text-xs cursor-pointer flex items-center gap-1 transition-colors font-semibold hover:bg-accent2 hover:border-accent2" onClick={saveEdit}>Save</button>
            <button className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer flex items-center gap-1 transition-colors hover:border-accent hover:text-text hover:bg-bg3" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Correctness issues */}
      {req.correctness_issues && req.correctness_issues.length > 0 && (
        <div className="mb-5" data-testid="correctness-issues">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Correctness Issues</h3>
          {req.correctness_issues.map(issue => (
            <div key={issue.code} className="text-xs text-red flex gap-1.5 mb-1">
              <span>{issue.message || issue.code}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tests */}
      {req.tests && req.tests.length > 0 && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Tests ({req.tests.length})</h3>
          {req.tests.map(t => (
            <div key={t.id} className="px-[10px] py-1.5 text-[13px] cursor-pointer border-b border-border transition-colors hover:bg-bg3" onClick={() => navigateTo("tests", t.id)}>
              <span className="text-accent font-semibold">{t.id}</span>
              <span className="text-text2 ml-2">{t.description}</span>
              {t.suite && <Badge variant="info">{t.suite}</Badge>}
            </div>
          ))}
        </div>
      )}

      {/* Driven requirements */}
      {req.driven_requirements && req.driven_requirements.length > 0 && (
        <div className="mb-5" data-testid="driven-requirements">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Driven Requirements ({req.driven_requirements.length})</h3>
          {req.driven_requirements.map((c: any) => (
            <div key={c.id} className="px-[10px] py-1.5 text-[13px] cursor-pointer border-b border-border transition-colors hover:bg-bg3" onClick={() => navigateTo("requirements", c.id)}>
              <span className="text-accent font-semibold">{c.id}</span>
              <span className="text-text2 ml-2">{c.description || c.title}</span>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      <div className="border-t border-border pt-3">
        <button
          className="text-[11px] text-text2 uppercase tracking-[0.5px] cursor-pointer bg-transparent border-none hover:text-accent transition-colors"
          onClick={() => setHistoryOpen(!historyOpen)}
        >
          {historyOpen ? "▾" : "▸"} History
        </button>
        {historyOpen && (
          <HistorySection entries={historyRaw ? mapToSnakeCase(historyRaw.entityHistory) : []} />
        )}
      </div>
    </div>
  );
}

const REQUIREMENT_LEVELS = ["system", "subsystem", "subgroup", "implementation"];

function EditField({ label, value, onChange, type = "text", options }: { label: string; value: string; onChange: (v: string) => void; type?: "text" | "select"; options?: string[] }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-[11px] text-text2 w-20">{label}</label>
      {type === "select" && options ? (
        <select value={value} onChange={e => onChange(e.target.value)} aria-label={label} className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs">
          {options.map(o => <option key={o || "__empty"} value={o}>{o || "Unassigned"}</option>)}
        </select>
      ) : (
        <input type="text" value={value} onChange={e => onChange(e.target.value)} className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs" />
      )}
    </div>
  );
}

function RequirementDriverSelector({ currentDriverIds, allRequirements, currentRequirementId, onChange }: { currentDriverIds: string[]; allRequirements: Requirement[]; currentRequirementId: string; onChange: (ids: string[]) => void }) {
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [driverSearch, setDriverSearch] = useState("");
  const [driverPickerOpen, setDriverPickerOpen] = useState(false);
  const linkedIds = new Set(currentDriverIds);
  const available = allRequirements
    .filter(r => r.id !== currentRequirementId && !linkedIds.has(r.id))
    .sort((a, b) => a.id.localeCompare(b.id));
  const filteredAvailable = available.filter(r => requirementDriverOptionLabel(r).toLowerCase().includes(driverSearch.toLowerCase()));
  if (selectedDriverId && !available.some(r => r.id === selectedDriverId)) {
    setSelectedDriverId("");
  }
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid="requirement-driver-selector">
      <label className="text-[11px] text-text2">Requirement Drivers</label>
      {currentDriverIds.length === 0 && <div className="text-xs text-text2">No requirement drivers selected.</div>}
      {currentDriverIds.map(driverId => {
        const driver = allRequirements.find(r => r.id === driverId);
        const label = `${driverId}${driver?.title ? ` — ${driver.title}` : ""}`;
        return (
          <div key={driverId} className="flex min-w-0 items-center gap-1">
            <span className="min-w-0 flex-1 truncate text-xs text-text" title={label}>{label}</span>
            <button
              type="button"
              className="shrink-0 text-red text-xs bg-transparent border-none cursor-pointer px-1"
              onClick={() => onChange(currentDriverIds.filter(id => id !== driverId))}
              aria-label={`Remove requirement driver ${driverId}`}
            >x</button>
          </div>
        );
      })}
      {available.length > 0 && (
        <div className="flex min-w-0 items-center gap-1">
          <div
            className="relative min-w-0 flex-1"
            onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDriverPickerOpen(false);
            }}
          >
            <input
              className="w-full min-w-0 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs outline-none focus:border-accent placeholder:text-text2"
              aria-label="Link requirement driver"
              value={driverSearch}
              placeholder={selectedDriverId ? `Selected: ${selectedDriverId}` : "Search requirement driver..."}
              onFocus={() => setDriverPickerOpen(true)}
              onClick={() => setDriverPickerOpen(true)}
              onChange={e => {
                setDriverSearch(e.target.value);
                setDriverPickerOpen(true);
              }}
            />
            {driverPickerOpen && (
              <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-auto rounded-default border border-border bg-bg2 shadow-lg" data-testid="requirement-driver-options">
                {filteredAvailable.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-text2">No matching requirement drivers.</div>
                ) : filteredAvailable.map(r => {
                  const label = requirementDriverOptionLabel(r);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className="block w-full truncate bg-transparent px-2 py-1.5 text-left text-xs text-text hover:bg-bg3"
                      title={label}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        setSelectedDriverId(r.id);
                        setDriverSearch("");
                        setDriverPickerOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 text-accent text-xs bg-transparent border border-accent rounded-default px-2 py-1 cursor-pointer hover:bg-accent hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedDriverId}
            onClick={() => {
              if (!selectedDriverId) return;
              onChange([...currentDriverIds, selectedDriverId]);
              setSelectedDriverId("");
              setDriverSearch("");
              setDriverPickerOpen(false);
            }}
          >Add Driver</button>
        </div>
      )}
    </div>
  );
}


function requirementDriverOptionLabel(requirement: Requirement): string {
  return truncateText(`${requirement.id} — ${requirement.title || requirement.description || requirement.level}`, 86);
}


function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 3)}...`;
}

function SourcesList({ sources }: { sources?: Source[] }) {
  if (!sources || sources.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col gap-0.5" data-testid="sources-list">
      {sources.map((s) =>
        s.url ? (
          <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline break-all">{s.label}</a>
        ) : (
          <span key={s.id} className="break-all">{s.label}</span>
        )
      )}
    </div>
  );
}

function DriverList({ drivers, onNavigate, testId }: { drivers?: Array<{ id: string; label?: string; url?: string }>; onNavigate?: (id: string) => void; testId?: string }) {
  if (!drivers || drivers.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      {drivers.map(driver => (
        onNavigate ? (
          <button key={driver.id} type="button" onClick={() => onNavigate(driver.id)} className="text-accent bg-transparent border-none cursor-pointer text-left text-xs hover:underline">
            {driver.id}{driver.label ? ` — ${driver.label}` : ""}
          </button>
        ) : driver.url ? (
          <a key={driver.id} href={driver.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline break-all">{driver.label || driver.id}</a>
        ) : (
          <span key={driver.id} className="break-all">{driver.label || driver.id}</span>
        )
      ))}
    </div>
  );
}

function TagsList({ tags }: { tags?: string[] }) {
  if (!tags || tags.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-wrap gap-1" data-testid="requirement-tags">
      {tags.map(tag => <Badge key={tag} variant="muted">{tag}</Badge>)}
    </div>
  );
}

function sourceId(source: Source | string): string {
  return typeof source === "string" ? source : source.id;
}

function SourceSelector({ currentSourceDrivers, allSourceDrivers, onLink, onUnlink, pendingSourceDriverIdRef }: { currentSourceDrivers: Source[]; allSourceDrivers: Source[]; onLink: (id: string) => void; onUnlink: (id: string) => void; pendingSourceDriverIdRef: MutableRefObject<string> }) {
  const [selectedSourceDriverId, setSelectedSourceDriverId] = useState("");
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const linkedIds = new Set(currentSourceDrivers.map(s => s.id));
  const available = allSourceDrivers.filter(s => !linkedIds.has(s.id));
  const filteredAvailable = available.filter(s => `${s.id} ${s.label}`.toLowerCase().includes(sourceSearch.toLowerCase()));
  if (selectedSourceDriverId && !available.some(s => s.id === selectedSourceDriverId)) {
    setSelectedSourceDriverId("");
  }
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid="source-selector">
      <label className="text-[11px] text-text2">Source Drivers</label>
      {currentSourceDrivers.map(s => (
        <div key={s.id} className="flex min-w-0 items-center gap-1">
          <span className="min-w-0 flex-1 truncate text-xs text-text" title={s.label}>{s.label}</span>
          <button type="button" className="shrink-0 text-red text-xs bg-transparent border-none cursor-pointer px-1" onClick={() => onUnlink(s.id)} aria-label={`Remove source ${s.label}`}>x</button>
        </div>
      ))}
      {available.length > 0 && (
        <div className="flex min-w-0 items-center gap-1">
          <div
            className="relative min-w-0 flex-1"
            onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setSourcePickerOpen(false);
            }}
          >
            <input
              className="w-full min-w-0 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs outline-none focus:border-accent placeholder:text-text2"
              aria-label="Link source"
              value={sourceSearch}
              placeholder={selectedSourceDriverId ? `Selected: ${selectedSourceDriverId}` : "Search source..."}
              onFocus={() => setSourcePickerOpen(true)}
              onClick={() => setSourcePickerOpen(true)}
              onChange={e => {
                setSourceSearch(e.target.value);
                setSourcePickerOpen(true);
              }}
            />
            {sourcePickerOpen && (
              <div className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-auto rounded-default border border-border bg-bg2 shadow-lg" data-testid="source-driver-options">
                {filteredAvailable.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-text2">No matching source drivers.</div>
                ) : filteredAvailable.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    className="block w-full truncate bg-transparent px-2 py-1.5 text-left text-xs text-text hover:bg-bg3"
                    title={s.label}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => {
                      pendingSourceDriverIdRef.current = s.id;
                      setSelectedSourceDriverId(s.id);
                      setSourceSearch("");
                      setSourcePickerOpen(false);
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 text-accent text-xs bg-transparent border border-accent rounded-default px-2 py-1 cursor-pointer hover:bg-accent hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedSourceDriverId}
            onClick={() => {
              if (!selectedSourceDriverId) return;
              onLink(selectedSourceDriverId);
              pendingSourceDriverIdRef.current = "";
              setSelectedSourceDriverId("");
              setSourceSearch("");
              setSourcePickerOpen(false);
            }}
          >Add Source</button>
        </div>
      )}
    </div>
  );
}

interface HistoryEntry {
  id: number;
  ts: string;
  user_id: string;
  action: string;
  changed_fields: string | null;
}

function HistorySection({ entries }: { entries: HistoryEntry[] }) {
  if (entries.length === 0) {
    return <div className="text-xs text-text2 py-2 mt-2">No history recorded.</div>;
  }
  return (
    <div className="mt-2 space-y-1">
      {entries.map(e => {
        const changes = e.changed_fields ? JSON.parse(e.changed_fields) : null;
        return (
          <div key={e.id} className="text-xs border-b border-border/50 py-1.5">
            <div className="flex items-center gap-2">
              <span className="text-text2">{formatTs(e.ts)}</span>
              <span className="text-text">{e.user_id}</span>
              <span className="text-accent">{e.action}</span>
            </div>
            {changes && (
              <div className="mt-0.5 pl-4 text-text2">
                {Object.entries(changes).map(([field, val]: [string, any]) => (
                  <div key={field}>
                    <span className="text-text">{field}</span>: {String(val.old)} → {String(val.new)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
